"use client";

import Image from "next/image";
import React from "react";
import portrait from "@/public/wangpeng.jpeg";
import { motion } from "framer-motion";
import { BsGithub, BsLinkedin } from "react-icons/bs";
import { HiDownload } from "react-icons/hi";
import { useSectionInView } from "@/lib/hooks";
import { useActiveSectionContext } from "@/context/active-section-context";
import { useI18nContext } from "@/context/i18n-context";

const EASE_OUT_QUART = [0.25, 1, 0.5, 1] as const;

export default function Intro() {
  const { language, t } = useI18nContext();
  const links: { name: string; hash: string }[] = t("links");
  const { ref } = useSectionInView(links[0]?.name ?? "Home", 0.5);
  const { setActiveSection, setTimeOfLastClick } = useActiveSectionContext();

  return (
    <section
      ref={ref}
      id="home"
      className="w-full max-w-[52rem] mt-32 sm:mt-36 mb-24 sm:mb-32 scroll-mt-28"
    >
      {/* Avatar row */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE_OUT_QUART }}
        className="flex items-center gap-4 mb-8"
      >
        <div className="relative shrink-0">
          <Image
            src={portrait}
            alt="Wangpeng An"
            width={72}
            height={72}
            quality={100}
            priority
            className="w-[4.5rem] h-[4.5rem] rounded-full object-cover
              ring-2 ring-[var(--color-border)]
              shadow-sm"
          />
          <span
            aria-hidden="true"
            className="absolute -bottom-0.5 -right-0.5 text-xl leading-none select-none"
          >
            👋
          </span>
        </div>

        {/* Status chip */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full
          bg-[var(--color-surface)]
          border border-[var(--color-border-subtle)]
          text-xs font-medium tracking-wide text-[var(--color-text-secondary)]"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          {t("intro.statusChip")}
        </div>
      </motion.div>

      {/* Headline block */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.08, ease: EASE_OUT_QUART }}
        className="mb-6"
      >
        <p className="text-sm font-medium tracking-[0.15em] uppercase
          text-[var(--color-brand)] mb-3">
          {t("intro.greeting")}
        </p>

        <h1 className="font-sans font-semibold leading-[1.1] tracking-tight
          text-[clamp(2rem,5vw+0.5rem,3.5rem)]
          text-[var(--color-text-primary)] mb-4">
          {t("intro.heading")}
        </h1>

        <p className="font-serif font-light leading-[1.75] max-w-[42rem]
          text-[1.1rem] text-[var(--color-text-secondary)]">
          {t("intro.main")}
        </p>
      </motion.div>

      {/* Affiliation line */}
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.16, ease: EASE_OUT_QUART }}
        className="text-sm text-[var(--color-text-tertiary)] tracking-wide mb-10 font-sans"
      >
        {t("intro.occupation")}
      </motion.p>

      {/* CTA row */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.24, ease: EASE_OUT_QUART }}
        className="flex flex-wrap items-center gap-3"
      >
        <a
          href="/CV.pdf"
          download
          className="inline-flex items-center gap-2 px-5 py-2.5
            rounded-full text-sm font-medium
            bg-[var(--color-brand)] text-white
            hover:opacity-90
            transition-opacity duration-200
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-2"
        >
          {t("intro.downloadCV")}
          <HiDownload className="w-4 h-4" aria-hidden="true" />
        </a>

        <a
          href="https://www.linkedin.com/in/tensorboy/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="LinkedIn profile"
          className="inline-flex items-center justify-center w-10 h-10
            rounded-full
            bg-[var(--color-surface)]
            border border-[var(--color-border)]
            text-[var(--color-text-secondary)]
            hover:text-[var(--color-brand)] hover:border-[var(--color-brand)]
            transition-colors duration-200
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
        >
          <BsLinkedin className="w-4 h-4" />
        </a>

        <a
          href="https://github.com/tensorboy/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="GitHub profile"
          className="inline-flex items-center justify-center w-10 h-10
            rounded-full
            bg-[var(--color-surface)]
            border border-[var(--color-border)]
            text-[var(--color-text-secondary)]
            hover:text-[var(--color-brand)] hover:border-[var(--color-brand)]
            transition-colors duration-200
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
        >
          <BsGithub className="w-4 h-4" />
        </a>
      </motion.div>
    </section>
  );
}
